import React, { Fragment } from 'react';
import { Trans } from 'react-i18next';
import moment from 'moment';
import { getJobParamFromCompanies } from 'src/components/SurveyForm/utils';
import { ServiceTypes } from 'src/utils/data/types';
import { toSimpleDateString } from 'src/utils/format/date';
import { NOT_YET_STARTED, IN_PROCESS, VERIFIER_REVISION_REQUESTED, VERIFIER_REVIEW_UNDERWAY, VERIFIER_REVISION_UNDERWAY, REVIEWER_REVIEW_UNDERWAY } from 'src/utils/data/job';

export const getSectionResults = (section, results) => {
  const sectionResults = [];

  section.questions.forEach(question => {
    const selectedResult = results?.find(result => result.datasheetQuestionId === question.datasheetQuestionId);

    if (selectedResult) {
      sectionResults.push(selectedResult);
    }
  });

  return sectionResults;
};

const [ALL, NOTE_EXISTED, ACTION_NEEDED, NEGATIVE_OUTCOME] = [
  'All',
  'Note',
  'Negative outcome',
  'With Negative outcome',
];

export const getButtonTextOfFilterBy = (tag, statistic, negativeOutcomeQuestionCount) => {
  switch (tag) {
    case ALL:
      return <Trans>ALL</Trans>;
    case NOTE_EXISTED:
      return (
        <Fragment>
          <Trans>NOTE</Trans>
          {` ${statistic ? statistic.noteExistedQuestionIds.length : 0}`}
        </Fragment>
      );
    case ACTION_NEEDED:
      return (
        <Fragment>
          <Trans>ACTION_NEEDED</Trans>
          {` ${statistic ? statistic.actionRequiredtypeQuestionIds.length : 0}`}
        </Fragment>
      );
    case NEGATIVE_OUTCOME:
      return (
        <Fragment>
          <Trans>NEGATIVE_OUTCOME</Trans>
          {` ${negativeOutcomeQuestionCount}`}
        </Fragment>
      );
    default:
      return '';
  }
};

const getQuestionsWithNegativeOutcome = (questions, negativeOutcomeResultIds, getResult) => {
  return questions.filter(q => {
    const { result } = getResult(q);
    return result ? negativeOutcomeResultIds.includes(result.validationResultId) : false;
  });
};

export const getSectionQuestions = (
  section,
  questions,
  currentFilter,
  statistic,
  negativeOutcomeResultIds = null,
  getResult = null) => {

  switch (currentFilter) {
    case ALL:
      return questions;

    case NOTE_EXISTED:
      if (!statistic) {
        return [];
      }

      return questions.filter(q =>
        statistic.noteExistedQuestionIds.includes(q.id),
      );
    case ACTION_NEEDED:
      if (!statistic) {
        return [];
      }

      return questions.filter(q =>
        statistic.actionRequiredtypeQuestionIds.includes(q.id),
      );

    case NEGATIVE_OUTCOME:
      if (!(negativeOutcomeResultIds?.length > 0) || !getResult) {
        return [];
      }

      return getQuestionsWithNegativeOutcome(
        questions,
        negativeOutcomeResultIds,
        getResult);

    default:
      return questions;
  }
};

export const getJobValue = (field, job) => {
  let value;

  if (job[field] !== undefined) {
    value = job[field];
  } else {
    const services = job.jobServices.filter(c => !!c.service && c.service.currentVersion.serviceType.type !== ServiceTypes.HIDDEN);
    const companies = job.jobCompanies.filter(c => !!c.company);
    const factory = getJobParamFromCompanies(companies, 'Factory');
    const vendor = getJobParamFromCompanies(companies, 'Vendor');
    const agent = getJobParamFromCompanies(companies, 'Agent');
    const program = getJobParamFromCompanies(companies, 'Program');

    const factoryAddress = factory
      ? factory.company.currentVersion.companyAddresses[0]
      : null;
    const country = factoryAddress
      ? factoryAddress.currentVersion.country.currentVersion.name
      : null;
    const auditors = job.jobAuditors.filter(c => !!c.user);

    const data = {
      services: services
        .map(service => service.service.currentVersion.name)
        .join(', '),
      factory: factory ? factory.company.currentVersion.name : '',
      vendor: vendor ? vendor.company.currentVersion.name : '',
      agent: agent ? agent.company.currentVersion.name : '',
      country: country || '',
      fieldStaff: auditors
        .map(a => `${a.user.firstName} ${a.user.lastName}`)
        .join(', '),
      program: program ? program.company.currentVersion.name : '',
    };

    value = data[field];
  }

  if (field.indexOf('At') === field.length - 2) {
    value = value ? toSimpleDateString(value, 'MMMM DD, YYYY') : '';
  }

  return {
    type: 'autofill',
    value: value || '-',
  };
};

export const generateDefaultDatasheetResults = (datasheet, job) => {
  const results = [];
  
  (datasheet.currentVersion.datasheetSections || []).forEach(section => {
    section.questions.forEach(question => {
      const isAutofillQuestion = question.datasheetQuestion.currentVersion.questionType === 'autofill';
      const autofillResult = isAutofillQuestion ? getJobValue(question.datasheetQuestion.currentVersion.autofillSettings.field, job)
          : null;

      results.push({
        attachments: [],
        comments: '',
        datasheetQuestionId: question.datasheetQuestionId,
        currentQuestionId: question.datasheetQuestion.currentVersion.id,
        result: autofillResult,
        showToFactoryUser: datasheet.currentVersion.showToFactoryUser,
        isPrefilled: false,
        isAutofill: !!autofillResult,
      });
    });
  });

  return results;
};

export const getNegativeOutcomeResultFamilyItems = datasheet => datasheet.currentVersion.defaultResultFamily?.currentVersion.resultFamilyOutcomes
  .filter(outcome => outcome.resultCategory.currentVersion.code === 'Negative Outcome') || [];

export const getQuestionsWithNegativeOutcomeCount = (questions, negativeOutcomeResultIds, getResult) => {
  return getQuestionsWithNegativeOutcome(questions, negativeOutcomeResultIds, getResult).length;
};

const getConditionResult = (
  parentQuestionType,
  parentResult,
  condition,
  parentQuestion = null,
  fromJob = true,
  rectifiedValue = false
) => {
  const {
    predicate,
    predicateAddition,
    rangeEnd,
    rangeStart,
    rowName,
    value,
  } = condition;
  let val = rectifiedValue ? 'rectifiedValue' : 'value';
  let parentResultNumber = +(parentResult || {})[val] || 0;
  let parentResultValue = (parentResult || {})[val] ?? null;
  if(parentResultValue === null || parentResultValue === undefined){
    val = 'value';
    parentResultNumber = +(parentResult || {})[val] || 0;
    parentResultValue = (parentResult || {})[val] || null;
  }

  switch (parentQuestionType) {
    case 'text':
    case 'autofill':
    case 'relation':
    case 'time':
      if (
        predicate === 'has_value' &&
        (parentResultValue || '').indexOf(value) !== -1
      ) {
        return true;
      }
      if (
        predicate === 'doesnt_has_value' &&
        (parentResultValue || '').indexOf(value) === -1
      ) {
        return true;
      }
      if (predicate === 'is_empty' && !parentResultValue) {
        return true;
      }
      if (predicate === 'not_empty' && !!parentResultValue) {
        return true;
      }
      break;
    case 'checkbox':
      if (
        (predicate === 'checked' && parentResultValue) ||
        (predicate === 'unchecked' && !parentResultValue)
      ) {
        return true;
      }
      break;
    case 'number':
    case 'year':
    case 'percent':
      if (parentResultValue === null) {
        return false;
      }
      if (predicateAddition === 'equal' && parentResultNumber === +value) {
        return true;
      }
      if (predicateAddition === 'lt' && parentResultNumber < +value) {
        return true;
      }
      if (predicateAddition === 'gt' && parentResultNumber > +value) {
        return true;
      }
      if (
        predicateAddition === 'in_the_range' &&
        parentResultNumber >= +rangeStart &&
        parentResultNumber <= +rangeEnd
      ) {
        return true;
      }
      break;
    case 'date':
    case 'month':
      if (predicateAddition === 'equal') {
        if (parentResultValue === null) {
          return false;
        }

        switch (parentQuestionType) {
          case 'date':
            return moment(parentResultValue)
              .startOf('day')
              .isSame(moment(value).startOf('day'), 'day');
          case 'month':
            return moment(parentResultValue)
              .startOf('day')
              .isSame(moment(value).startOf('day'), 'month');
          default:
            return false;
        }
      }
      if (
        predicateAddition === 'lt' &&
        moment(parentResultValue)
          .startOf('day')
          .isBefore(moment(value).startOf('day'))
      ) {
        return true;
      }
      if (
        predicateAddition === 'gt' &&
        moment(parentResultValue)
          .startOf('day')
          .isAfter(moment(value).startOf('day'))
      ) {
        return true;
      }
      if (
        predicateAddition === 'in_the_range' &&
        moment(parentResultValue)
          .startOf('day')
          .isSameOrAfter(moment(rangeStart).startOf('day')) &&
        moment(parentResult.value)
          .startOf('day')
          .isSameOrBefore(moment(rangeEnd).startOf('day'))
      ) {
        return true;
      }
      break;
    case 'dropdown':
    case 'dependentDropdown':
      if (!Array.isArray(parentResultValue)) {
        if (
          predicate === 'has_value' &&
          (parentResultValue || '') === value
        ) {
          return true;
        }
        if (
          predicate === 'doesnt_has_value' &&
          (parentResultValue || '') !== value
        ) {
          return true;
        }
      } else {
        if (
          predicate === 'has_value' &&
          (parentResultValue || '').indexOf(value) !== -1
        ) {
          return true;
        }
        if (
          predicate === 'doesnt_has_value' &&
          (parentResultValue || '').indexOf(value) === -1
        ) {
          return true;
        }
      }
      break;
    case 'table': {
      const parentColumn = (fromJob ? parentQuestion.datasheetQuestion.currentVersion : parentQuestion).tableSettings.columns.find(
        v => v.name === rowName,
      );
      if (!parentColumn) {
        return false;
      }
      return getConditionResult(
        parentColumn.type,
        ((parentResult.rows || [])[0] || []).find(v => v.column === rowName),
        condition,
        null,
        fromJob,
      );
    }
    case 'file':
      if (predicate === 'file_not_uploaded') {
        return !parentResultValue || parentResultValue.length === 0;
      }
      if (predicate === 'file_uploaded') {
        return parentResultValue && parentResultValue.length !== 0;
      }
      break;
    case 'currency':{
      let currencyValueKey = 'currencyId';
      if(val === 'rectifiedValue') currencyValueKey = val;
      if (
        predicate === 'has_value' &&
        (parentResult || {})[currencyValueKey] === +value
      ) {
        return true;
      }
      if (
        predicate === 'doesnt_has_value' &&
        (parentResult || {})[currencyValueKey] !== +value
      ) {
        return true;
      }
      break;
    }
    case 'radioButton': {
      const radioButtonOptions = (fromJob ? parentQuestion?.datasheetQuestion?.currentVersion : parentQuestion).radioButtonSettings.rows || [];
      const valueIndex = radioButtonOptions.indexOf(value);
      if (Array.isArray(parentResultValue)) {
        if (
          predicate === 'has_value' &&
          (parentResultValue|| []).indexOf(valueIndex) !== -1
        ) {
          return true;
        }
        if (
          predicate === 'doesnt_has_value' &&
          (parentResultValue || []).indexOf(valueIndex) === -1
        ) {
          return true;
        }
      } else {
        const parentValue = parentResultValue !== null && parentResultValue !== undefined ? +parentResultValue : -1;
        if (
          predicate === 'has_value' &&
          parentValue === valueIndex
        ) {
          return true;
        }
        if (
          predicate === 'doesnt_has_value' &&
          parentValue !== valueIndex
        ) {
          return true;
        }
      }
      
      break;
    }
    default:
      return false;
  }

  return false;
};

export const applyQuestionConditions = (section, getResult, datasheet, fromJob = true, rectifiedValue = false) => section.questions.filter(question => {
  if (!question.conditions || question.conditions.length === 0) {
    return true;
  }

  const conditionAction = question.conditions[0].action || '';
  const conditionParts = [];

  question.conditions.forEach(condition => {
    let parentQuestion ;
    if(fromJob){
      // eslint-disable-next-line
      (datasheet?.currentVersion?.datasheetSections || []).some(sect =>{
        parentQuestion = sect.questions.find(q => q.datasheetQuestionId === condition.datasheetQuestionId)
        if(parentQuestion) return true;
      });
    }else{
      parentQuestion = section.questions.find(q => q.datasheetQuestionId === condition.datasheetQuestionId);
    }

    const parentResult = getResult(parentQuestion).result || {};
    const parentQuestionType = fromJob ? parentQuestion.datasheetQuestion.currentVersion.questionType
                                       : parentQuestion.questionType;

    if (condition.logicalOperator === 'and') {
      conditionParts.push('&&');
    } else if (condition.logicalOperator === 'or') {
      conditionParts.push('||');
    }

    let part = getConditionResult(
      parentQuestionType,
      parentResult,
      condition,
      parentQuestion,
      fromJob,
      rectifiedValue
    );
    const conditionShow = conditionAction === 'show'  && part;
    const conditionHide = conditionAction === 'hide'  && !(part);    
    if(rectifiedValue && !conditionShow && !conditionHide ) {        
      part =  getConditionResult(
        parentQuestionType,
        parentResult,
        condition,
        parentQuestion,
        fromJob,
        false
      );
    }
    conditionParts.push(part ? 'true' : 'false');
  });
  conditionParts.splice(0, 1);

  return (conditionAction === 'show' ? 
    eval(conditionParts.join(' ')) : // eslint-disable-line no-eval
    !eval(conditionParts.join(' '))); // eslint-disable-line no-eval
});

export const getDependentQuestionsOnParentQuestion = (questions, datasheetQuestionId) => {
  const dependentQuestions = questions?.filter(question => question.conditions?.length).filter(que => que.conditions?.find(c => c.datasheetQuestionId === datasheetQuestionId));
  if (dependentQuestions?.length) {
    const innerDependentQuestions = dependentQuestions.map(q => getDependentQuestionsOnParentQuestion(questions, q.datasheetQuestionId)).flat() || [];
    return [...dependentQuestions, ...innerDependentQuestions];
  }
  return dependentQuestions;
};

export const getDependentQuestionsWithRectiedValueOnParentQuestion = (questions, datasheetQuestionId, parentQuestion, result) => {
  const conditionalDependentQuestions = questions?.filter(question => question.conditions?.length).filter(que => que.conditions?.find(c => c.datasheetQuestionId === datasheetQuestionId));
  const filteredDependentQuestions = conditionalDependentQuestions?.filter( (question) => {
    let conditionAction = '';
    const conditionParts = [];
    question.conditions.forEach(condition => {     
    conditionAction = condition.action || '';
    const parentQuestionType = parentQuestion?.datasheetQuestion?.currentVersion.questionType;

    if (condition.logicalOperator === 'and') {
      conditionParts.push('&&');
    } else if (condition.logicalOperator === 'or') {
      conditionParts.push('||');
    }

    const part = getConditionResult(
      parentQuestionType,
      result,
      condition,
      parentQuestion,
      true,
      true
    );
    conditionParts.push(part ? 'true' : 'false');
  });

  conditionParts.splice(0, 1);

  return (conditionAction === 'show' ? 
    eval(conditionParts.join(' ')) : // eslint-disable-line no-eval
    !eval(conditionParts.join(' '))); // eslint-disable-line no-eval
});
  return filteredDependentQuestions; 
};

export const getValidationTextBasedOnLanguage = (validationErrors, validationTranslations, selectedLanguageCode) => {
  if (!validationTranslations) {
    return validationErrors;
  }

  const newValidationErros = validationErrors.reduce((acc, curr) => {
    const availableTranslation = validationTranslations.find(v => v.validation.toLowerCase() === curr.toLowerCase());
    if (availableTranslation && availableTranslation[selectedLanguageCode]) {
      acc.push(availableTranslation[selectedLanguageCode]);
    } else {
      acc.push(curr);
    }
    return acc;
  }, []);
  return newValidationErros;
};

export const preFilledDataDisclaimer = () => {
  return (
    <ul className="prefill-disclaimer">
      <li>
        <Trans>PREFILL_DISCLAIMER_TEXT.PREFILLED_DATA_MARKED_AS_YELLOW</Trans>
      </li>
      <li>
        <Trans>PREFILL_DISCLAIMER_TEXT.AUDITOR_RESPONSIBLE_TO_VERIFIY_PREFILL_DATA</Trans>
      </li>
      <li>
        <Trans>PREFILL_DISCLAIMER_TEXT.ANSWERS_MAY_NOT_BE_COMPLETE</Trans>
      </li>
      <li>
        <Trans>PREFILL_DISCLAIMER_TEXT.AUDITOR_RESPONSIBLE_FOR_ACCURATE_INFO</Trans>
      </li>
    </ul>
  );
};

export const showResultVerificationSection = (isExternalUser, isVerifier, isReviewer, isAdmin, status, hasOpenComment) => {
  // SAQ job(SAQ verifier)/SAQ Manager  status = not_yet_started / in_process show result verification section
  const externalUserHasOpenComments = isExternalUser && hasOpenComment;
  const jobStatus = [NOT_YET_STARTED, IN_PROCESS]; // hide for job status
  if((externalUserHasOpenComments || isVerifier || isReviewer || isAdmin) && !jobStatus.includes(status)) return true;

  return false;
};

export const editResultVerificationSection = (isVerifier, isReviewer, isAdmin, status) => {
  // edit when user is auditor status is Verifier review underway
  const availableStatusForVerifier = [VERIFIER_REVIEW_UNDERWAY, VERIFIER_REVISION_UNDERWAY];
  if (isVerifier && availableStatusForVerifier.includes(status)) return true;
  // edit when user is reviewer status is Reviewer review underway
  const availableStatusForReviewer = [REVIEWER_REVIEW_UNDERWAY];
  if (isReviewer && availableStatusForReviewer.includes(status)) return true; 
  return false;
};

// New SAQ
export const canAddComment = (isAuthorisedExternalUser, isVerifier, isReviewer, isAdmin, status) => {
  // SAQ verifier can add comment if status is "Verifier review underway" or "Verifier revision underway"
  const availableStatusForVerifier = [VERIFIER_REVIEW_UNDERWAY, VERIFIER_REVISION_UNDERWAY];
  if (isVerifier && availableStatusForVerifier.includes(status)) return true;

  // Reviewer can add comment if status is "Reviewer review underway"
  const availableStatusForReviewer = [REVIEWER_REVIEW_UNDERWAY];
  if (isReviewer && availableStatusForReviewer.includes(status)) return true;

  // External user who is part of job should able to Add comment if status is "Verifier revision requested"
  const availableStatusForExternalUser = [VERIFIER_REVISION_REQUESTED];
  if ((isAdmin || isAuthorisedExternalUser) && availableStatusForExternalUser.includes(status)) return true;
  
  return false;
};

export const isAttachmentDisabled = (isAuthorisedExternalUser, isAdmin, status) => {
  const availableStatusForExternalUser = [VERIFIER_REVISION_REQUESTED];
  if ((isAdmin || isAuthorisedExternalUser) && availableStatusForExternalUser.includes(status)) return false;
  return true;
};